import React, { useState } from 'react';
import simplifiedApiService from '../../services/simplifiedApiService';

// Define types for API responses
interface ImportSession {
  id: string;
  import_type: 'sales_data' | 'ad_spend_data';
  user_id: string;
  original_filename: string;
  csv_headers: string[];
  status: string;
  validation_results?: ValidationResult;
  created_at: string;
  updated_at: string;
}

interface ValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

interface ValidationResult {
  valid: boolean;
  issues: { rowIndex: number; issues: ValidationIssue[] }[];
}

/**
 * Component for testing the simplified CSV parser
 */
const SimplifiedCSVUploadPage: React.FC = () => {
  const [salesFile, setSalesFile] = useState<File | null>(null);
  const [adspendFile, setAdspendFile] = useState<File | null>(null);
  const [currentImportId, setCurrentImportId] = useState('');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [importResults, setImportResults] = useState<ImportSession | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle sales file selection
  const handleSalesFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSalesFile(e.target.files[0]);
    }
  };

  // Handle adspend file selection
  const handleAdspendFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setAdspendFile(e.target.files[0]);
    }
  };

  // Handle column mapping changes
  const handleMappingChange = (csvHeader: string, entityField: string) => {
    setColumnMappings({
      ...columnMappings,
      [entityField]: csvHeader,
    });
  };

  // Upload sales file
  const uploadSalesFile = async () => {
    if (!salesFile) return;

    setLoading(true);
    setError('');

    try {
      const result = await simplifiedApiService.uploadSalesFile(salesFile);
      setCurrentImportId(result.id);
      setCsvHeaders(result.csv_headers || []);
      setImportResults(result);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error uploading sales file');
      } else {
        setError('Unknown error uploading sales file');
      }
    } finally {
      setLoading(false);
    }
  };

  // Upload adspend file
  const uploadAdspendFile = async () => {
    if (!adspendFile) return;

    setLoading(true);
    setError('');

    try {
      const result = await simplifiedApiService.uploadAdspendFile(adspendFile);
      setCurrentImportId(result.id);
      setCsvHeaders(result.csv_headers || []);
      setImportResults(result);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error uploading adspend file');
      } else {
        setError('Unknown error uploading adspend file');
      }
    } finally {
      setLoading(false);
    }
  };

  // Process the import with column mappings
  const processImport = async () => {
    if (!currentImportId || Object.keys(columnMappings).length === 0) {
      setError('Please upload a file and define column mappings first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await simplifiedApiService.processImport(currentImportId, columnMappings);
      setImportResults(result);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error processing import');
      } else {
        setError('Unknown error processing import');
      }
    } finally {
      setLoading(false);
    }
  };

  // Define common field mappings for sales and adspend data
  const salesFields = [
    'first_name',
    'last_name',
    'email',
    'phone',
    'address',
    'city',
    'state',
    'zip',
    'sale_date',
    'sale_amount',
    'source',
    'campaign',
    'notes',
  ];

  const adspendFields = [
    'date',
    'campaign',
    'channel',
    'source',
    'medium',
    'spend_amount',
    'clicks',
    'impressions',
    'cpc',
    'cpm',
    'ctr',
    'conversions',
    'conversion_rate',
    'cost_per_conversion',
  ];

  // Determine which fields to display based on the current import
  const fieldsToMap = importResults?.import_type === 'ad_spend_data' ? adspendFields : salesFields;

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Simplified CSV Upload (Phase 1)</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 border rounded shadow">
          <h2 className="text-lg font-semibold mb-2">Upload Sales Data</h2>
          <input
            type="file"
            accept=".csv"
            onChange={handleSalesFileChange}
            className="mb-2 block w-full"
          />
          <button
            onClick={uploadSalesFile}
            disabled={!salesFile || loading}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
          >
            {loading && currentImportId === '' ? 'Uploading...' : 'Upload Sales CSV'}
          </button>
        </div>

        <div className="p-4 border rounded shadow">
          <h2 className="text-lg font-semibold mb-2">Upload Ad Spend Data</h2>
          <input
            type="file"
            accept=".csv"
            onChange={handleAdspendFileChange}
            className="mb-2 block w-full"
          />
          <button
            onClick={uploadAdspendFile}
            disabled={!adspendFile || loading}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
          >
            {loading && currentImportId === '' ? 'Uploading...' : 'Upload Ad Spend CSV'}
          </button>
        </div>
      </div>

      {error && <div className="p-4 mb-4 bg-red-100 border border-red-400 text-red-700">{error}</div>}

      {csvHeaders.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Column Mapping</h2>
          <p className="mb-4">
            Map your CSV columns to our system fields. Select which CSV column corresponds to each field.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fieldsToMap.map((field) => (
              <div key={field} className="flex flex-col">
                <label className="font-medium mb-1">{field.replace(/_/g, ' ')}</label>
                <select
                  value={columnMappings[field] || ''}
                  onChange={(e) => handleMappingChange(e.target.value, field)}
                  className="p-2 border rounded"
                >
                  <option value="">-- Select CSV Column --</option>
                  {csvHeaders.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <button
              onClick={processImport}
              disabled={loading || Object.keys(columnMappings).length === 0}
              className="px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-300"
            >
              {loading ? 'Processing...' : 'Process Import'}
            </button>
          </div>
        </div>
      )}

      {importResults && (
        <div className="mt-6 p-4 border rounded">
          <h2 className="text-xl font-semibold mb-2">Import Results</h2>
          <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
            {JSON.stringify(importResults, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default SimplifiedCSVUploadPage;